import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, fromEvent, Observable, of, ReplaySubject, Subject, timer } from 'rxjs';
import { catchError, debounce, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TenantState } from '../store/state/tenant.state';
import { TenantSessionState } from '../store/state/tenant-session.state';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from '../models/featureSettings';
import { GroupNameResponse } from '../models/groupNameResponse';
import { UserService } from '../users/user.service';
import { AssetCacheStoreService } from './asset-cache-store.service';
import { AssetDetailCacheService } from './asset-detail-cache.service';
import { LocationCacheStoreService } from './location-cache-store.service';
import { ServiceCenterLocation } from './models/service-center-location';
import { ServiceCenterAsset } from './models/service-center-asset';
import { ServiceCenterFilter, ServiceCenterUserType, applyTypeDefaults, defaultFilter, distanceFilterEnabled, isFilterActive } from './models/service-center-filter';

import { activeDistance } from './distance-filter';
import { MapSizePreset, SMALL_MAP_WIDTH_PX, clampMapWidth, mapSizePreset } from './map-size';

export type AssetViewMode = 'table' | 'cards';

// Only filter prefs persist; drilldown/drawer/scope live in the URL.
export interface ServiceCenterViewState {
  filter: ServiceCenterFilter;
  mapHidden?: boolean;
  // Absent = Large (50%). Raw as saved; clamped against the current body at render.
  mapWidthPx?: number;
  viewMode?: AssetViewMode;
}

// Drives scope defaulting + auto-drill on single-shop tenants.
export type LocationsTier =
  | { kind: 'none' }
  | { kind: 'single'; loc: ServiceCenterLocation }
  | { kind: 'multiple' };

export const ServiceCenterViewKinds = {
  FLAT_ASSETS:     'flat-assets',
  FLAT_LOCATIONS:  'flat-locations',
} as const;

export type ServiceCenterViewKind = typeof ServiceCenterViewKinds[keyof typeof ServiceCenterViewKinds];

// 'auto' goes through the smart-refresh diff; all others commit eagerly.
export type RefreshSource = 'auto' | 'manual' | 'wake' | 'commit';

export interface PendingRefreshState {
  count: number;
  messageKey: string;
}

// Records fetched so far / server total during a paged refresh. Null when no
// paged load is in flight (idle, or the single-fetch location view).
export interface RefreshProgress {
  loaded: number;
  total: number;
}

// Plain so the state service stays free of a Google Maps dependency.
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
  zoom: number;
}

export function classifyLocations(locations: ServiceCenterLocation[]): LocationsTier {
  const real = (locations ?? []).filter(l => !l.isOtr);
  if (real.length === 0) return { kind: 'none' };
  if (real.length === 1) return { kind: 'single', loc: real[0] };
  return { kind: 'multiple' };
}

@Injectable({ providedIn: 'root' })
export class ServiceCenterStateService {
  @SelectSnapshot(TenantState.getCurrentTenantExternalId) private externalTenantId!: string;
  @SelectSnapshot(TenantSessionState.getTenantFeatures) private tenantFeatures!: FeatureSettings | null;

  // Decided from the URL the page was entered with, before the page writes its restored view into the URL.
  entryAllowsDefault = true;

  // Bump suffix when stored shape OR value semantics change.
  private readonly STORAGE_KEY_PREFIX = 'serviceCenter.viewState.v8';
  private get storageKey(): string {
    return `${this.STORAGE_KEY_PREFIX}.${this.externalTenantId ?? 'anon'}`;
  }

  // Null = "not yet loaded" — distinguishes pre-fetch from empty result so
  // subscribers don't fire entry-redirect logic before the HTTP completes.
  private readonly _locations = new BehaviorSubject<ServiceCenterLocation[] | null>(null);
  // Reflects the UNFILTERED tenant snapshot.
  private readonly _tenantTier = new ReplaySubject<LocationsTier>(1);
  private readonly _assets = new BehaviorSubject<ServiceCenterAsset[]>([]);
  // Full unpaged set so every pin renders regardless of table window. `latest`
  // includes partials streamed mid-populate — snapshot lookups must see every
  // row the table shows, or click-to-zoom silently no-ops until commit.
  private readonly _mapAssetsState = new BehaviorSubject<{
    latest: ServiceCenterAsset[];
    committed: ServiceCenterAsset[];
    kind: 'commit' | 'progress';
  }>({ latest: [], committed: [], kind: 'commit' });
  // True for the duration of any asset load — gates the auto-poll so a tick can't cancel an in-flight fetch.
  private readonly _loadInFlight = new BehaviorSubject<boolean>(false);
  // Set the instant the user touches a filter (before the form/validate debounce),
  // cleared when that load settles. Interaction-triggered — NOT loadInFlight — so
  // the silent steady-state poll never raises it. Drives the footer load bar
  // through the otherwise-blank debounce gap.
  private readonly _filterApplying = new BehaviorSubject<boolean>(false);
  private readonly _selectedLocation = new BehaviorSubject<ServiceCenterLocation | null>(null);
  private readonly _selectedAssetId = new BehaviorSubject<string | null>(null);
  private readonly _viewKind = new BehaviorSubject<ServiceCenterViewKind>(ServiceCenterViewKinds.FLAT_ASSETS);
  // null = no explicit user preference; true/false = user explicitly hid/shown.
  private readonly _mapSuppressedOverride = new BehaviorSubject<boolean | null>(null);
  private readonly _narrowViewport = new BehaviorSubject<boolean>(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1200px)').matches
  );
  // Transient resize-driven hide. Set when the viewport crosses into narrow,
  // cleared on an explicit Show/Hide click or when the viewport widens.
  private readonly _narrowAutoHide = new BehaviorSubject<boolean>(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1200px)').matches
  );
  private readonly _viewMode = new BehaviorSubject<AssetViewMode>('cards');
  private readonly _mapWidthPx = new BehaviorSubject<number | null>(null);
  private readonly _bodyWidthPx = new BehaviorSubject<number>(typeof window !== 'undefined' ? window.innerWidth : 1600);

  // Subject (not Behavior) so subscribers don't fire spurious ticks.
  private readonly _refreshTrigger = new Subject<RefreshSource>();
  private readonly _lastRefreshedAt = new BehaviorSubject<Date | null>(null);
  private readonly _refreshProgress = new BehaviorSubject<RefreshProgress | null>(null);
  private readonly _pendingRefresh = new BehaviorSubject<PendingRefreshState | null>(null);
  private readonly _mapViewportBounds = new BehaviorSubject<MapBounds | null>(null);
  private readonly _mapFitRequest = new Subject<void>();
  private readonly _distancePickMode = new BehaviorSubject(false);
  private readonly _distancePointPicked = new Subject<{ lat: number; lng: number }>();
  readonly distancePickMode$ = this._distancePickMode.asObservable();
  readonly distancePointPicked$ = this._distancePointPicked.asObservable();

  startDistancePick(): void {
    this.setMapHiddenByUser(false);
    this._distancePickMode.next(true);
  }

  cancelDistancePick(): void { this._distancePickMode.next(false); }

  pickDistancePoint(point: { lat: number; lng: number }): void {
    if (!this._distancePickMode.value) return;
    this._distancePickMode.next(false);
    this._distancePointPicked.next(point);
  }
  // True while the asset map holds at least one click-expanded cluster; drives
  // the Re-cluster control's visibility.
  private readonly _hasExpandedClusters = new BehaviorSubject<boolean>(false);
  private readonly _focusSelectedAssetRequest = new Subject<void>();
  // Cluster click / Reset View / asset pin. Continuous gestures don't fire it.
  private readonly _discreteMapJump = new Subject<void>();
  // True when camera has drifted from the last fit; drives Reset View [disabled].
  private readonly _canResetView = new BehaviorSubject<boolean>(false);

  // One-shot per shell lifecycle, consumed by the first summary to evaluate.
  private _entryRedirectEvaluated = false;

  // Drill anchor: set ONLY when an implicit map zoom-drill navigates into
  // FLAT_ASSETS; cleared when leaving FLAT_ASSETS (setViewKind) or by an
  // unrelated filter change. The map's zoom-back branch only fires when the
  // single-shop filter matches the anchor — so explicitly picked shops (card,
  // pin, or dropdown) aren't dismissed by a later zoom-out.
  private _drillAnchorShop: string | null = null;
  private _fitOnNextFilter = false;

  // Populated only by unfiltered loads — never trust if user landed with filters in the URL.
  private readonly _unfilteredTotalsById = new Map<string, number>();

  private readonly _filter = new BehaviorSubject<ServiceCenterFilter>(defaultFilter());

  private readonly _shopGroups = new BehaviorSubject<GroupNameResponse[]>([]);
  private readonly _homeGroups = new BehaviorSubject<GroupNameResponse[]>([]);
  private readonly _userType = new BehaviorSubject<ServiceCenterUserType | null>(null);

  // Emits the latestAlertId whose case changed in the diagnostics case drawer
  // (opened from the alert dialog) so the drawer can refresh just that chip.
  private readonly _caseUpdated = new Subject<string>();

  readonly locations$ = this._locations.asObservable();
  readonly assets$ = this._assets.asObservable();
  readonly mapAssets$ = this._mapAssetsState.pipe(map(s => s.committed), distinctUntilChanged());
  // Partial sets streamed during a progressive load — the map draws them but applies no camera effects.
  readonly mapAssetsProgress$ = this._mapAssetsState.pipe(filter(s => s.kind === 'progress'), map(s => s.latest));
  readonly selectedLocation$ = this._selectedLocation.asObservable();
  readonly selectedAssetId$ = this._selectedAssetId.asObservable();
  readonly viewKind$ = this._viewKind.asObservable();
  readonly refresh$ = this._refreshTrigger.asObservable();
  readonly lastRefreshedAt$ = this._lastRefreshedAt.asObservable();
  readonly loadInFlight$ = this._loadInFlight.asObservable();
  readonly filterApplying$ = this._filterApplying.asObservable();
  readonly refreshProgress$ = this._refreshProgress.asObservable();
  readonly filter$ = this._filter.asObservable();
  readonly pendingRefresh$ = this._pendingRefresh.asObservable();
  readonly mapViewportBounds$ = this._mapViewportBounds.asObservable();
  readonly mapFitRequest$ = this._mapFitRequest.asObservable();
  readonly hasExpandedClusters$ = this._hasExpandedClusters.asObservable();
  readonly focusSelectedAssetRequest$ = this._focusSelectedAssetRequest.asObservable();
  readonly discreteMapJump$ = this._discreteMapJump.asObservable();
  readonly canResetView$ = this._canResetView.asObservable();
  readonly viewMode$ = this._viewMode.asObservable();
  readonly narrowViewport$ = this._narrowViewport.asObservable();
  // Stacked layouts ignore the explicit width, so it must not drive the highlight there.
  readonly mapWidthPx$: Observable<number | null> = combineLatest([this._mapWidthPx, this._bodyWidthPx, this._narrowViewport]).pipe(
    map(([px, body, narrow]) => (px == null || narrow ? null : clampMapWidth(px, body))),
    distinctUntilChanged(),
  );

  readonly shopGroups$ = this._shopGroups.asObservable();
  readonly homeGroups$ = this._homeGroups.asObservable();
  readonly userType$ = this._userType.asObservable();
  readonly caseUpdated$ = this._caseUpdated.asObservable();

  constructor(
    private userService: UserService,
    private assetCacheStore: AssetCacheStoreService,
    private assetDetailCache: AssetDetailCacheService,
    private locationCacheStore: LocationCacheStoreService,
  ) {
    // Reconcile fires only once type resolves — after the sync hydrateFilter +
    // dispatchUrlState in ngOnInit — so an affixed shop overwrites a deep-linked ?shop=.
    this._userType.subscribe(type => {
      if (!type) return;
      const next = this.applyTypeDefaultsToFilter(this._filter.value);
      if (!this.sameScopeSelection(next, this._filter.value)) this.setFilter(next);
    });
  }

  applyTypeDefaultsToFilter(filter: ServiceCenterFilter): ServiceCenterFilter {
    return applyTypeDefaults(filter, this._userType.value, this._shopGroups.value, this._homeGroups.value);
  }

  isFilterActive(filter: ServiceCenterFilter): boolean {
    return isFilterActive(filter, this._userType.value, this._shopGroups.value, this._homeGroups.value);
  }

  distanceFilterEnabled(): boolean {
    return distanceFilterEnabled(this.tenantFeatures);
  }

  dvirEnabled(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.ServiceCenterDVIR);
  }

  // Gates setFilter and hydrateFilter.
  private normalizeGatedFacets(filter: ServiceCenterFilter): ServiceCenterFilter {
    const distance = this.distanceFilterEnabled() ? activeDistance(filter) : null;
    return {
      ...filter,
      near: undefined, radius: undefined, unit: undefined, ...distance,
      dvirWithDefects: this.dvirEnabled() ? filter.dvirWithDefects : undefined,
    };
  }

  private sameScopeSelection(a: ServiceCenterFilter, b: ServiceCenterFilter): boolean {
    const eq = (x: readonly string[] = [], y: readonly string[] = []) =>
      x.length === y.length && x.every(v => y.includes(v));
    return eq(a.shopLocations, b.shopLocations) && eq(a.homeLocationGroupIds, b.homeLocationGroupIds);
  }

  readonly locationsTier$: Observable<LocationsTier> = this._tenantTier.pipe(
    distinctUntilChanged((a, b) =>
      a.kind === b.kind &&
      (a.kind !== 'single' || (b.kind === 'single' && a.loc.locationId === b.loc.locationId))
    ),
  );

  readonly mapSuppressed$: Observable<boolean> = combineLatest([
    this.mapAssets$,
    this._mapSuppressedOverride,
    this._narrowViewport,
    this._narrowAutoHide,
    this._userType,
  ]).pipe(
    map(([assets, override, narrow, narrowAutoHide, userType]) =>
      this.computeMapSuppressed(assets, override, narrow, narrowAutoHide, userType)),
    distinctUntilChanged(),
  );

  // User clicked Hide on a wide viewport — the only suppressed case that stays
  // a side-by-side slide. Tier-3/no-coords and the narrow-viewport hide aren't.
  private readonly userHiddenOnWide$: Observable<boolean> = combineLatest([
    this._mapSuppressedOverride,
    this._narrowViewport,
    this._narrowAutoHide,
  ]).pipe(
    map(([override, narrow, narrowAutoHide]) => override === true && !narrow && !narrowAutoHide),
    distinctUntilChanged(),
  );

  // Keep the map mounted through a wide-viewport user-hide so the panel can
  // animate its collapse instead of popping out; everything else unmounts it
  // (no idle Google Map, no map instance on mobile-hidden).
  readonly mapMounted$: Observable<boolean> = combineLatest([
    this.mapSuppressed$,
    this.userHiddenOnWide$,
  ]).pipe(
    map(([suppressed, hiddenOnWide]) => !suppressed || hiddenOnWide),
    distinctUntilChanged(),
  );

  // Present whenever the split layout exists: the panel stays mounted through a
  // wide user-hide, so the divider is still there to drag the map back out.
  readonly mapSplitterVisible$: Observable<boolean> = combineLatest([
    this.mapMounted$,
    this._narrowViewport,
  ]).pipe(
    map(([mounted, narrow]) => mounted && !narrow),
    distinctUntilChanged(),
  );

  readonly mapSizePreset$: Observable<MapSizePreset | null> = combineLatest([
    this.mapSuppressed$,
    this.mapWidthPx$,
    this._bodyWidthPx,
  ]).pipe(
    map(([hidden, px, body]) => mapSizePreset(hidden, px, body / 2)),
    distinctUntilChanged(),
  );

  // narrowAutoHide is the transient resize-driven hide and wins until the
  // user clicks Show/Hide (which clears it) or the viewport widens. That
  // lets the toggle work on narrow while still preventing the overlay
  // immediately after a resize.
  private computeMapSuppressed(
    assets: ServiceCenterAsset[],
    override: boolean | null,
    narrow: boolean,
    narrowAutoHide: boolean,
    userType: ServiceCenterUserType | null,
  ): boolean {
    if (narrowAutoHide) return true;
    if (override !== null) return override;
    // S0·H0 has no shops and no home assets — nothing to plot. Hidden by default,
    // but an explicit Show (override) above still wins.
    if (userType && userType.shopCount === 0 && userType.homeCount === 0) return true;
    if (narrow) return true;
    return assets.length > 0 &&
      assets.every(a => (a.latitude == null || a.latitude === 0)
                      && (a.longitude == null || a.longitude === 0));
  }

  // `filtered` skips the tier + unfiltered-totals updates so a narrow result
  // set doesn't falsely promote a multi-shop tenant into the single-shop
  // shortcut, and so the "X of Y" denominator stays anchored to the most
  // recent unfiltered snapshot.
  setLocations(locations: ServiceCenterLocation[], filtered: boolean): void {
    this._locations.next(locations);
    if (!filtered) {
      this._tenantTier.next(classifyLocations(locations));
      this._unfilteredTotalsById.clear();
      for (const loc of locations) {
        this._unfilteredTotalsById.set(loc.locationId, loc.totalAssets);
      }
    }
  }

  // Preserves _tenantTier — that's a separate fact surviving scope flips.
  clearLocations(): void {
    this._locations.next([]);
  }

  setAssets(assets: ServiceCenterAsset[]): void {
    this._assets.next(assets);
  }

  setMapAssets(assets: ServiceCenterAsset[]): void {
    this._mapAssetsState.next({ latest: assets, committed: assets, kind: 'commit' });
  }

  setMapAssetsProgress(assets: ServiceCenterAsset[]): void {
    this._mapAssetsState.next({ ...this._mapAssetsState.value, latest: assets, kind: 'progress' });
  }

  setLoadInFlight(inFlight: boolean): void {
    this._loadInFlight.next(inFlight);
    // Load settled -> the apply is done. (A poll's settle clears a flag it never set.)
    if (!inFlight && this._filterApplying.value) this._filterApplying.next(false);
  }

  signalFilterInteraction(): void {
    if (!this._filterApplying.value) this._filterApplying.next(true);
  }

  // The cache has painted the filtered view — the apply is done regardless of any
  // background API validate still running (the Refresh control owns that). For
  // client-side asset filtering this fires effectively instantly.
  markFilterApplied(): void {
    if (this._filterApplying.value) this._filterApplying.next(false);
  }

  isLoadInFlight(): boolean {
    return this._loadInFlight.value;
  }

  setRefreshProgress(progress: RefreshProgress | null): void {
    this._refreshProgress.next(progress);
  }

  setSelectedLocation(location: ServiceCenterLocation | null): void {
    this._selectedLocation.next(location);
  }

  setSelectedAssetId(id: string | null): void {
    this._selectedAssetId.next(id);
  }

  setViewKind(kind: ServiceCenterViewKind): void {
    if (this._viewKind.value === ServiceCenterViewKinds.FLAT_ASSETS
        && kind !== ServiceCenterViewKinds.FLAT_ASSETS) {
      this._drillAnchorShop = null;
    }
    this._viewKind.next(kind);
  }

  getLocationsSnapshot(): ServiceCenterLocation[] | null {
    return this._locations.value;
  }

  getMapAssetsSnapshot(): ServiceCenterAsset[] {
    return this._mapAssetsState.value.latest;
  }

  getSelectedAssetIdSnapshot(): string | null {
    return this._selectedAssetId.value;
  }

  // Dev override from console. null = resume auto-detection.
  setMapSuppressed(value: boolean | null): void {
    this._mapSuppressedOverride.next(value);
  }

  setMapHiddenByUser(hidden: boolean): void {
    this.setMapSize(hidden, this._mapWidthPx.value);
  }

  setMapSizePreset(preset: MapSizePreset): void {
    if (preset === 'hidden') { this.setMapSize(true, null); return; }
    this.setMapSize(false, preset === 'small' ? SMALL_MAP_WIDTH_PX : null);
  }

  // Explicit sizing outranks the resize-driven hide, so the map can be
  // revealed on a narrow viewport.
  setMapSize(hidden: boolean, widthPx: number | null): void {
    // Hiding keeps the stored width — Off is about visibility, and a restore by
    // preset or by dragging the divider back out sets its own width anyway.
    const width = hidden ? (widthPx ?? this._mapWidthPx.value) : widthPx;
    this._narrowAutoHide.next(false);
    this._mapSuppressedOverride.next(hidden);
    this._mapWidthPx.next(width);
    this.patchViewState({ mapHidden: hidden, mapWidthPx: width ?? undefined });
  }

  setBodyWidthPx(px: number): void {
    if (px > 0 && px !== this._bodyWidthPx.value) this._bodyWidthPx.next(px);
  }

  getCanResetViewSnapshot(): boolean {
    return this._canResetView.value;
  }

  setViewMode(mode: AssetViewMode): void {
    if (this._viewMode.value === mode) return;
    this._viewMode.next(mode);
    this.patchViewState({ viewMode: mode });
  }

  getViewModeSnapshot(): AssetViewMode {
    return this._viewMode.value;
  }

  // Default 'manual' keeps unspecified callers on the safe commit+refetch path.
  triggerRefresh(source: RefreshSource = 'manual'): void {
    // A table refresh (poll / wake / manual / banner ack) supersedes any cached
    // drawer detail — drop it so the next open refetches.
    this.assetDetailCache.clear();
    this._refreshTrigger.next(source);
  }

  markRefreshed(at: Date = new Date()): void {
    this._lastRefreshedAt.next(at);
  }

  setPendingRefresh(state: PendingRefreshState): void {
    this._pendingRefresh.next(state);
  }

  clearPendingRefresh(): void {
    if (this._pendingRefresh.value !== null) {
      this._pendingRefresh.next(null);
    }
  }

  getPendingRefreshSnapshot(): PendingRefreshState | null {
    return this._pendingRefresh.value;
  }

  setMapViewportBounds(bounds: MapBounds | null): void {
    this._mapViewportBounds.next(bounds);
  }

  getMapViewportBoundsSnapshot(): MapBounds | null {
    return this._mapViewportBounds.value;
  }

  requestMapFit(): void {
    this._mapFitRequest.next();
  }

  requestFitOnNextFilter(): void {
    this._fitOnNextFilter = true;
  }

  consumeFitOnNextFilter(): boolean {
    const requested = this._fitOnNextFilter;
    this._fitOnNextFilter = false;
    return requested;
  }

  setHasExpandedClusters(value: boolean): void {
    if (this._hasExpandedClusters.value !== value) this._hasExpandedClusters.next(value);
  }

  requestFocusSelectedAsset(): void {
    this._focusSelectedAssetRequest.next();
  }

  // Re-clicking the selected asset produces no router event — recenter instead.
  focusIfAlreadySelected(vehicleId: string): boolean {
    if (vehicleId !== this._selectedAssetId.value) return false;
    this.requestFocusSelectedAsset();
    return true;
  }

  signalDiscreteMapJump(): void {
    this._discreteMapJump.next();
  }

  setCanResetView(canReset: boolean): void {
    if (this._canResetView.value !== canReset) this._canResetView.next(canReset);
  }

  // Sign-out / tenant switch reset: wipe the cached records/locations (all
  // tenants) and reset the in-memory streams. Persisted view state is per-tenant
  // and dropped separately (clearViewState) only on sign-out, not on switch.
  clearAll(): void {
    this.cancelDistancePick();
    this.assetCacheStore.clearAll();
    this.assetDetailCache.clear();
    this.locationCacheStore.clearAll();
    this._locations.next(null);
    this._assets.next([]);
    this._mapAssetsState.next({ latest: [], committed: [], kind: 'commit' });
    this._loadInFlight.next(false);
    this._refreshProgress.next(null);
    this._selectedLocation.next(null);
    this._selectedAssetId.next(null);
    this._filter.next(defaultFilter());
    this._pendingRefresh.next(null);
    this._entryRedirectEvaluated = false;
    this._drillAnchorShop = null;
    this._unfilteredTotalsById.clear();
    this._shopGroups.next([]);
    this._homeGroups.next([]);
    this._userType.next(null);
  }

  markShopSetByDrill(shopValue: string): void {
    this._drillAnchorShop = shopValue;
  }

  clearShopSetByDrill(): void {
    this._drillAnchorShop = null;
  }

  wasShopSetByDrill(currentShopValue: string): boolean {
    return this._drillAnchorShop !== null && this._drillAnchorShop === currentShopValue;
  }

  getUnfilteredTotal(locationId: string): number | undefined {
    return this._unfilteredTotalsById.get(locationId);
  }

  resetEntryRedirectSlot(): void {
    this._entryRedirectEvaluated = false;
  }

  tryConsumeEntryRedirectSlot(): boolean {
    if (this._entryRedirectEvaluated) return false;
    this._entryRedirectEvaluated = true;
    return true;
  }

  setFilter(filter: ServiceCenterFilter): void {
    const next = this.normalizeGatedFacets(filter);
    this._filter.next(next);
    this.patchViewState({ filter: next });
  }

  getFilter(): ServiceCenterFilter {
    return this._filter.value;
  }

  getUserTypeSnapshot(): ServiceCenterUserType | null {
    return this._userType.value;
  }

  // Scopes the asset-card cache. Keyed by tenant + the user's resolved
  // shop/home group ids — the actual permission boundary, so two same-type
  // users in one tenant can't share a cache. 'pending' until type resolves.
  getCacheContextKey(): string {
    if (!this._userType.value) return 'pending';
    const ids = (gs: ReadonlyArray<{ id: string }>) => gs.map(g => g.id).sort().join(',');
    return `${this.externalTenantId ?? 'anon'}:s[${ids(this._shopGroups.value)}]:h[${ids(this._homeGroups.value)}]`;
  }

  notifyCaseUpdated(alertId: string): void {
    if (alertId) this._caseUpdated.next(alertId);
  }

  getShopGroupsSnapshot(): GroupNameResponse[] {
    return this._shopGroups.value;
  }

  getHomeGroupsSnapshot(): GroupNameResponse[] {
    return this._homeGroups.value;
  }

  // A failed list fetch degrades to an empty axis so the type always resolves (no stuck spinner).
  refreshUserType(): void {
    forkJoin({
      shops: this.userService.getUserShopGroups().pipe(catchError(() => of<GroupNameResponse[]>([]))),
      homes: this.userService.getUserLocationGroups().pipe(catchError(() => of<GroupNameResponse[]>([]))),
    }).subscribe(({ shops, homes }) => {
      this._shopGroups.next(shops ?? []);
      this._homeGroups.next(homes ?? []);
      this._userType.next({ shopCount: (shops ?? []).length, homeCount: (homes ?? []).length });
    });
  }

  hydrateFilter(): void {
    const persisted = this.readViewState();
    const filter = persisted?.filter ?? this._filter.value;
    this._filter.next(this.normalizeGatedFacets(filter));
    if (persisted?.mapHidden === true) this._mapSuppressedOverride.next(true);
    else if (persisted?.mapHidden === false) this._mapSuppressedOverride.next(false);
    this._mapWidthPx.next(typeof persisted?.mapWidthPx === 'number' && isFinite(persisted.mapWidthPx) ? persisted.mapWidthPx : null);
    // viewMode intentionally not hydrated — Table toggle is hidden, so a persisted 'table' would strand the user.
    this.setupViewportListener();
  }

  // Idempotent — repeat hydrations (multi-mount, tenant switch) won't stack listeners.
  // The matchMedia listener is intentionally never removed: state service is app-lifetime singleton.
  private viewportListenerWired = false;
  private setupViewportListener(): void {
    if (this.viewportListenerWired || typeof window === 'undefined') return;
    this.viewportListenerWired = true;
    const mql = window.matchMedia('(max-width: 1200px)');
    fromEvent<MediaQueryListEvent>(mql, 'change').pipe(
      map(e => e.matches),
      startWith(mql.matches),
      // Narrow applies immediately (cheap unmount); widen remounts the map and
      // rebuilds every marker, so defer it until a window-edge drag settles.
      // of(0) flushes synchronously — debounce drops a duration that only completes.
      debounce(narrow => (narrow ? of(0) : timer(200))),
    ).subscribe(narrow => {
      this._narrowViewport.next(narrow);
      this._narrowAutoHide.next(narrow);
    });
  }

  // An undefined patch value deletes the key rather than relying on JSON.stringify to drop it.
  patchViewState(patch: Partial<ServiceCenterViewState>): void {
    const current = this.readViewState();
    const next = { ...current, filter: current?.filter ?? this._filter.value, ...patch };
    (Object.keys(patch) as (keyof ServiceCenterViewState)[]).forEach(key => {
      if (patch[key] === undefined) delete next[key];
    });
    this.saveViewState(next);
  }

  private saveViewState(state: ServiceCenterViewState): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      // Quota / serialization failures are non-fatal.
    }
  }

  readViewState(): ServiceCenterViewState | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) as ServiceCenterViewState : null;
    } catch {
      return null;
    }
  }

  clearViewState(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
  }
}
